import { UserLocator } from '../user/user-locator.svc';
import { UserPermModel } from './model/user-perm.mdl';
import { map, filter, switchMap, distinctUntilChanged } from 'rxjs/operators';
import { GeneralPlanPermHandler, IPlanPermHandler } from './handler/general-plan-perm-handler';
import { Injectable, Injector } from '@angular/core';
import { Tracker } from 'flux-core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { PlanPermission } from 'flux-definition';
import { PlanType } from './plan-type';
import { PlanModel } from './model/plan.mdl';

/**
 * A Plan permission manager which has user's permissions and helps to check the
 * permission availability of the user for user's plan.
 */

@Injectable()
export class UserPlanPermManager {

    /**
     * A General permission check handler.
     */
    public static GENERAL_PERM_HANDLER: string = 'GeneralPermHandler';

    /**
     * Id of the user's plan.
     */
    protected planId: string;

    /**
     * Type of the user's plan.
     */
    protected planType: string;

    /**
     * User's plan permissions.
     */
    protected permissions: {[ permission: string]: UserPermModel };

    /**
     * Hold current diagram paid status
     */
    protected isPaidDiagramSub: BehaviorSubject<boolean> = new BehaviorSubject( false );

    /**
     * Hold current diagram paid and Canceled status
     */
    protected isPaidCanceledDiagramSub: BehaviorSubject<boolean> = new BehaviorSubject( false );

    /**
     * Handlers to check user's plan.
     */
    protected permHandlers: {[ permission: string ]: IPlanPermHandler };

    constructor( protected userLocator: UserLocator,
                 protected injector: Injector ) {
        // Run setCurrentPlan only when directly new-ing UserPlanPermManager, not subclasses
        if ( new.target === UserPlanPermManager ) {
            this.setCurrentPlan();
        }
        this.initPermissionHandlers();
    }

    /**
     * get current active plan id
     * @returns plan id;
     */
    public getCurrentActivePlanId(): string {
        return this.planId;
    }

    /**
     * Returns true if the user plan is free.
     */
    public isFreeUser(): Observable<boolean> {
        return this.userLocator.getUserDataAfterUserSubscriptionStarted().pipe(
            filter( user => !!user && !!user.plan ),
            map( user =>
                user.plan.type.startsWith( PlanType.Public )),
        );
    }

    /**
     * Returns true if the user plan is free or demo and not paid diagram.
     */
    public isFreeOrDemoUserAndNotPaidDiagram(): Observable<boolean> {
        return this.userLocator.getUserDataAfterUserSubscriptionStarted().pipe(
            filter( user => !!user && !!user.plan ),
            switchMap( user => this.isPaidDiagramSub.pipe(
                map( isPaid => {
                    const planType = user.plan.type;
                    return (
                        ( planType.startsWith( PlanType.Demo ) ||
                        planType.startsWith( PlanType.Public )) &&
                        !isPaid
                    )
                })
            ),
        ));
    }

    /**
     * Return true if the current diagram is paid diagram
     * @returns Observable<boolean>
     */
    public isPaidDiagram(): Observable<boolean> {
        return this.isPaidDiagramSub;
    }

    /**
     * return true id the user paid to current diagram and canceled
     * @returns Observable<boolean>
     */
    public isPaidCanceledDiagram(): Observable<boolean> {
        return this.isPaidCanceledDiagramSub;
    }

    /**
     * Returns true if the user plan is team plan.
     * We are checking here plan id insted plan type
     * since team plan can be bublic or team plan both types
     */
    public isTeamUser(): Observable<boolean> {
        return this.userLocator.getUserDataAfterUserSubscriptionStarted().pipe(
            filter( user => !!user && !!user.plan ),
            map( user => !!user.team?.id ),
        );
    }

    /**
     * Returns true if the user plan is demo.
     */
    public isDemoUser(): Observable<boolean> {
        return this.userLocator.getUserDataAfterUserSubscriptionStarted().pipe(
            filter( user => !!user && !!user.plan ),
            map( user =>
                user.plan.type.startsWith( PlanType.Demo )),
        );
    }

    /**
     * Returns true if the user plan is business.
     */
    public isBusinessUser(): Observable<boolean> {
        return this.userLocator.getUserDataAfterUserSubscriptionStarted().pipe(
            filter( user => !!user && !!user.plan ),
            map( user =>
                user.plan.id.includes( 'business' )),
        );
    }

    /**
     * Returns whether the User is enterprise user or not.
     */
    public isEnterpriseUser( userId?: string ): Observable<boolean> {
        return this.userLocator.getUserDataAfterUserSubscriptionStarted( userId ).pipe(
            filter( user => !!user && !!user.plan ),
            map( user =>
                user.plan.type.startsWith( PlanType.Enterprise ) ||
                user.plan.type.startsWith( PlanType.Enterprise.toLowerCase()),
            ),
        );
    }

    /**
     * Returns true if the user plan is Lite.
     */
    public isLiteUser(): Observable<boolean> {
        return this.userLocator.getUserDataAfterUserSubscriptionStarted().pipe(
            filter( user => !!user && !!user.plan ),
            map( user =>
                user.plan.type.startsWith( PlanType.Lite )),
        );
    }

    /**
     * This function checks for the user plan is free or demo.
     * Returns true if the user plan belongs to either free or
     * demo.
     */
    public isFreeOrDemoUser(): Observable<boolean> {
        return this.isFreeUser().pipe(
            switchMap( isFreeUser => {
                if ( !isFreeUser ) {
                    return this.isDemoUser();
                } else {
                    return of( true );
                }
            }),
        );
    }

    /**
     * Check user has active diagram sub
     * @returns true if user has active paid diagram
     */
    public isUserHasActivePaidDiagram(): Observable<boolean> {
        return this.userLocator.getUserDataAfterUserSubscriptionStarted().pipe(
            filter( user => !!user ),
            map( user => {
                const diagramSub = user.diagramSubscription;
                return diagramSub && (
                    diagramSub.status === 'ACTIVE' ||
                    diagramSub.status === 'NON_RENEWING'
                );
            }),
        );
    }

    /**
     * Check all given permissions one by one. Checks are done by calling the check
     * method of corresponding permission handler's check method. If there is no
     * specific handler defined in the map then it uses the generic handler to check
     * the permission. If one of the permission is not allowed then immediately
     * returns false without checking rest of the permissions in given array.
     * @param permNames the permissions to check
     * @param permData Any supporting data to evaluate the permission.( Optional )
     */
    public check( permNames: string[], permData?: any ) {
        if ( !this.permissions ) {
            return false;
        }
        for ( const permName of permNames ) {
            if ( permName.includes( '|' )) {
                // checks at least one of the permission is available
                if ( !permName.split( '|' ).some( perm => this.check([ perm ]))) {
                    return false;
                }
                continue;
            }
            let handler: IPlanPermHandler = this.permHandlers[ permName ];
            if ( !handler ) {
                handler = this.permHandlers[ UserPlanPermManager.GENERAL_PERM_HANDLER ];
            }
            if ( !this.permissions[ permName ] || !handler.check( this.permissions[ permName ], permData )) {
                return false;
            }
        }
        return true;
    }

    /**
     * Gets the max usage for the given permission.
     * @param perm the permission to get the max usage for
     */
    public getMaxUsage( perm: PlanPermission ): number {
        return this.permissions[perm] && this.permissions[perm].maxUsage || 0;
    }

    /**
     * This method register the permission handlers.
     * @param handlerName - name of the handler
     * @param handler - handler instance
     */
    public registerHandler( handlerName: PlanPermission, handler: IPlanPermHandler ) {
        this.permHandlers[ handlerName ] = handler;
    }

    /**
     * Returns true if the user is a demo or enterprise user.
     */
    public isDemoOrEnterpriseUser(userId?: string): Observable<boolean> {
        return this.userLocator.getUserDataAfterUserSubscriptionStarted(userId).pipe(
        filter(user => !!user && !!user.plan),
        map(user => {
            const type = user.plan.type.toLowerCase();
            return (type.startsWith(PlanType.Demo.toLowerCase()) ||
                    type.startsWith(PlanType.Enterprise.toLowerCase()));
        })
        );
    }

    protected userPlanSubscription(): Observable<PlanModel | undefined> {
        return this.userLocator.getUserDataAfterUserSubscriptionStarted().pipe(
            distinctUntilChanged(),
            filter( user => !!user ),
            map( user => user.plan ),
        )
    }

    /**
     * Set current plan base on user subscription
     */
    protected setCurrentPlan() {
        this.userPlanSubscription().pipe(
            distinctUntilChanged(),
            filter( plan => !!plan ),
        ).subscribe( plan => {
            this.planId = plan.id;
            this.planType = plan.type;
            this.permissions = plan.permissions;
            Tracker.setGlobalProperty( 'plan', this.planType );
        });
    }

    /**
     * Initializes permission -> handler map.
     */
    protected initPermissionHandlers() {
        this.permHandlers = {};
        this.permHandlers[ UserPlanPermManager.GENERAL_PERM_HANDLER ] = this.injector.get( GeneralPlanPermHandler );
    }
}
